import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { CartNavButton } from "@/components/cart-nav-button"
import { ExternalLinkNotice } from "@/components/external-link-notice"
import { CART_DROPDOWN_FIELDS } from "@/lib/constants/cart-fields"
import {
  PLUSBAKE_LOGO_URL,
  PREORDER_ENABLED,
  TOAST_ORDER_URL,
} from "@/lib/constants/site"
import { useCartDrawer } from "@/lib/context/cart"
import { useCart } from "@/lib/hooks/use-cart"
import {
  useCateringVisible,
  useSiteCheckoutOpen,
} from "@/lib/hooks/use-site-settings"
import { trackOrderClick } from "@/lib/utils/analytics"
import { getCartItemCount } from "@/lib/utils/cart"
import { getCountryCodeFromPath } from "@/lib/utils/region"
import { Link, useLocation } from "@tanstack/react-router"
import { lazy, Suspense, useEffect, useMemo, useState } from "react"

const CartDropdown = lazy(() =>
  import("@/components/cart").then((module) => ({
    default: module.CartDropdown,
  })),
)
const SearchBar = lazy(() =>
  import("@/components/search-bar").then((module) => ({
    default: module.SearchBar,
  })),
)

// Catering is a runtime toggle (Admin → Settings → Store), so these are built
// per render rather than frozen at module load.
const buildNavLinks = (cateringVisible: boolean) => [
  { label: "Menu", to: "/$countryCode/menu" as const },
  { label: "Coffee", to: "/$countryCode/coffee" as const },
  ...(cateringVisible
    ? [{ label: "Catering", to: "/$countryCode/catering" as const }]
    : []),
  { label: "Visit", to: "/$countryCode/visit" as const },
  { label: "About", to: "/$countryCode/about" as const },
  { label: "FAQ", to: "/$countryCode/faq" as const },
  { label: "Contact", to: "/$countryCode/contact" as const },
  { label: "Accessibility", to: "/$countryCode/accessibility" as const },
]

const buildDesktopLeftLinks = (cateringVisible: boolean) => [
  { label: "Menu", to: "/$countryCode/menu" as const },
  { label: "Coffee", to: "/$countryCode/coffee" as const },
  ...(PREORDER_ENABLED
    ? [{ label: "Pre-Order", to: "/$countryCode/store" as const }]
    : []),
  ...(cateringVisible
    ? [{ label: "Catering", to: "/$countryCode/catering" as const }]
    : []),
  { label: "Visit", to: "/$countryCode/visit" as const },
]
const DESKTOP_RIGHT_LINKS = [
  { label: "FAQ", to: "/$countryCode/faq" as const },
  { label: "About", to: "/$countryCode/about" as const },
  { label: "Contact", to: "/$countryCode/contact" as const },
]

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    className="w-5 h-5"
    aria-hidden="true"
  >
    <circle cx="9" cy="9" r="6" />
    <path strokeLinecap="round" d="M13.5 13.5 17 17" />
  </svg>
)

const AccountIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    className="w-5 h-5"
    aria-hidden="true"
  >
    <circle cx="10" cy="7" r="3.25" />
    <path
      strokeLinecap="round"
      d="M3.5 17c.75-3 3.5-4.5 6.5-4.5s5.75 1.5 6.5 4.5"
    />
  </svg>
)

export const Navbar = () => {
  const cateringVisible = useCateringVisible()
  const navLinks = useMemo(
    () => buildNavLinks(cateringVisible),
    [cateringVisible],
  )
  const desktopLeftLinks = useMemo(
    () => buildDesktopLeftLinks(cateringVisible),
    [cateringVisible],
  )
  const location = useLocation()
  const countryCode = getCountryCodeFromPath(location.pathname) || "us"
  const { isOpen: cartOpen, openCart, addedAnimationKey } = useCartDrawer()
  const { data: cart } = useCart({ fields: CART_DROPDOWN_FIELDS })
  const cartItemCount = getCartItemCount(cart)
  // Account and cart only mean something while the site's own checkout is
  // live (pre-order shop or catering). A guest who still has items in a cart
  // keeps the cart button so they can finish or empty it.
  const checkoutOpen = useSiteCheckoutOpen()
  const showCart = checkoutOpen || cartItemCount > 0
  const [scrolled, setScrolled] = useState(false)
  const [cartReady, setCartReady] = useState(false)
  const [desktopSearchReady, setDesktopSearchReady] = useState(false)
  const [mobileMenuReady, setMobileMenuReady] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (cartOpen || addedAnimationKey > 0) {
      setCartReady(true)
    }
  }, [addedAnimationKey, cartOpen])

  const openLazyCart = () => {
    setCartReady(true)
    openCart()
  }

  const searchButton = (
    <button
      type="button"
      onClick={() => setDesktopSearchReady(true)}
      aria-label="Open search"
      className="w-9 h-9 rounded-full flex items-center justify-center text-[#5F5156] hover:text-[#F5928A] transition-colors"
    >
      <SearchIcon />
    </button>
  )

  const cartButton = (
    <CartNavButton
      itemCount={cartItemCount}
      onOpen={openLazyCart}
      animationKey={addedAnimationKey}
    />
  )

  return (
    <div className="sticky top-0 inset-x-0 z-40">
      <header
        className={`relative h-16 md:h-20 mx-auto transition-all duration-300 ${
          scrolled
            ? "bg-[#FFF9F8]/95 backdrop-blur-sm shadow-sm border-b border-[#FBD1C8]/40"
            : "bg-[#FFF9F8] border-b border-[#FBD1C8]/40"
        }`}
      >
        <nav className="content-container flex items-center justify-between w-full h-full">
          <Drawer
            open={mobileMenuOpen}
            onOpenChange={(open) => {
              setMobileMenuOpen(open)
              if (open) setMobileMenuReady(true)
            }}
          >
            <button
              type="button"
              aria-label="Open navigation menu"
              aria-haspopup="dialog"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden text-[#5F5156] hover:text-[#5D5054] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
            </button>
            <DrawerContent
              side="left"
              className="bg-[#FFF9F8] border-r border-[#FBD1C8]/40 w-[86vw] max-w-[300px]"
            >
              <DrawerHeader className="border-b border-[#FBD1C8]/40 pb-4">
                <DrawerTitle>
                  <img
                    src={PLUSBAKE_LOGO_URL}
                    alt="+Bake Cafe"
                    className="h-11 w-auto"
                    width={545}
                    height={189}
                    decoding="async"
                  />
                </DrawerTitle>
              </DrawerHeader>

              {PREORDER_ENABLED && (
                <div className="px-4 pt-4 pb-2">
                  {mobileMenuReady && (
                    <Suspense
                      fallback={
                        <div className="h-10 rounded-full bg-[#FCEAE7]" />
                      }
                    >
                      <SearchBar />
                    </Suspense>
                  )}
                </div>
              )}

              <div className="flex flex-col py-4 gap-1">
                <DrawerClose asChild>
                  <Link
                    to="/$countryCode"
                    params={{ countryCode }}
                    className="px-6 py-3 text-[#5F5156] hover:bg-[#FBD1C8]/30 transition-colors font-body text-base"
                  >
                    Home
                  </Link>
                </DrawerClose>
                {navLinks.map((link) => (
                  <DrawerClose key={link.to} asChild>
                    <Link
                      to={link.to}
                      params={{ countryCode }}
                      className="px-6 py-3 text-[#5F5156] hover:bg-[#FBD1C8]/30 transition-colors font-body text-base"
                    >
                      {link.label}
                    </Link>
                  </DrawerClose>
                ))}
                {checkoutOpen && (
                  <DrawerClose asChild>
                    <Link
                      to="/$countryCode/account"
                      params={{ countryCode }}
                      className="px-6 py-3 text-[#5F5156] hover:bg-[#FBD1C8]/30 transition-colors font-body text-base"
                    >
                      Account
                    </Link>
                  </DrawerClose>
                )}
                <div className="px-6 pt-6 flex flex-col gap-2">
                  <DrawerClose asChild>
                    <a
                      href={TOAST_ORDER_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackOrderClick("order_online_click", "navbar_mobile")
                      }
                      className="btn-coral w-full text-center text-sm py-3"
                    >
                      Order Online
                      <ExternalLinkNotice />
                    </a>
                  </DrawerClose>
                  {PREORDER_ENABLED && (
                    <DrawerClose asChild>
                      <Link
                        to="/$countryCode/store"
                        params={{ countryCode }}
                        className="btn-outline w-full text-center text-sm py-3"
                      >
                        Order a Cake
                      </Link>
                    </DrawerClose>
                  )}
                </div>
              </div>
            </DrawerContent>
          </Drawer>

          <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 flex items-center">
            <Link to="/$countryCode" params={{ countryCode }}>
              <img
                src={PLUSBAKE_LOGO_URL}
                alt="+Bake Cafe"
                className="h-9 w-auto sm:h-10 md:h-11"
                width={545}
                height={189}
                decoding="async"
              />
            </Link>
          </div>

          <div
            className="hidden lg:flex items-center gap-8"
            data-testid="desktop-left-nav"
          >
            {desktopLeftLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                params={{ countryCode }}
                className="text-[#5F5156] hover:text-[#F5928A] transition-colors text-sm font-medium tracking-wide font-body"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-5">
            <div
              className="flex items-center gap-5"
              data-testid="desktop-right-nav"
            >
              {DESKTOP_RIGHT_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  params={{ countryCode }}
                  className="text-[#5F5156] hover:text-[#F5928A] transition-colors text-sm font-medium tracking-wide font-body"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={TOAST_ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackOrderClick("order_online_click", "navbar_desktop")
                }
                className="btn-coral text-xs py-2.5 px-5"
              >
                Order Online
                <ExternalLinkNotice />
              </a>
            </div>

            {PREORDER_ENABLED &&
              (desktopSearchReady ? (
                <Suspense fallback={searchButton}>
                  <SearchBar collapsible initialExpanded />
                </Suspense>
              ) : (
                searchButton
              ))}

            {checkoutOpen && (
              <Link
                to="/$countryCode/account"
                params={{ countryCode }}
                aria-label="Your account"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#5F5156] hover:text-[#F5928A] transition-colors"
              >
                <AccountIcon />
              </Link>
            )}

            {showCart && cartButton}
          </div>

          {/* min-w-6 matches the menu button so the logo stays centered even
              when the account and cart icons are hidden. */}
          <div className="lg:hidden flex min-w-6 items-center justify-end gap-0.5 sm:gap-1">
            {PREORDER_ENABLED && (
              <button
                type="button"
                onClick={() => setMobileSearchOpen((prev) => !prev)}
                aria-label="Open search"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#5F5156] hover:text-[#F5928A] transition-colors"
              >
                <SearchIcon />
              </button>
            )}
            {checkoutOpen && (
              <Link
                to="/$countryCode/account"
                params={{ countryCode }}
                aria-label="Your account"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#5F5156] hover:text-[#F5928A] transition-colors"
              >
                <AccountIcon />
              </Link>
            )}
            {showCart && cartButton}
          </div>
        </nav>

        {(cartReady || cartOpen) && (
          <Suspense fallback={null}>
            <CartDropdown showTrigger={false} />
          </Suspense>
        )}

        {PREORDER_ENABLED && mobileSearchOpen && (
          <div className="lg:hidden px-4 pb-3 bg-[#FFF9F8] border-b border-[#FBD1C8]/40">
            <Suspense
              fallback={<div className="h-10 rounded-full bg-[#FCEAE7]" />}
            >
              <SearchBar onClose={() => setMobileSearchOpen(false)} />
            </Suspense>
          </div>
        )}
      </header>
    </div>
  )
}
